import React, { useEffect, useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import CharityCreateIndicators from "./CharityCreateIndicators";
import { Divider, Form, Spin, Steps, message } from "antd";
import styled from "styled-components";
import * as constants from "../../constants";
import { getContent, toggleSpinner, setContent, getOpportunity, getProgramById, getIndicatorAnswers } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import { getLocalStorageItem } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    opportunityDataSelector,
    savedContentListSelector,
    indicatorDataSelector
} from "../../redux/selectors";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";

message.config({
    top: 100,
    duration: 6,
    maxCount: 3,
});

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const CharityInterimCreateReport = ({ className }) => {
    const baseClassName = clsx("charityInterimCreateReport", className);
    const intl = useIntl();
    const [savedClicked, setSavedClicked] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {}
    const indicators = useAppSelector(indicatorDataSelector) || {};
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const { ID } = useParams();
    const { opportunityID } = JSON.parse(getLocalStorageItem('contentData') || '{}');
    const { opportunity:
        { programID,
        } = {} }
        = opportunityData || {};
    const { message: messageData } = useAppSelector(savedContentListSelector) || {};

    useEffect(() => {
        if (messageData && !savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/case-studies/${ID}`);
        } else if (messageData && savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content`);
        }
    }, [messageData])

    useEffect(() => {
        if (opportunityID) {
            document?.documentElement?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getContent({ contentID: ID }));
            dispatch(getOpportunity(opportunityID));
        }
    }, [opportunityID, programID]);

    useEffect(() => {
        if (programID) {
            dispatch(getProgramById(programID));
            dispatch(getIndicatorAnswers({
                programID,
                opportunityID: opportunityID
            }))
        }
    }, [programID]);

    const onFinishFailed = (e) => {
        console.log("Failed:", e);
    };

    const handleProceed = (e) => {
        const allIndicatorsValid = indicators?.every((item) => 
            (item.initialStatus === 'Complete' && item.finalStatus === 'Complete') ||
            (item.isMandatory === 'Y' && item.finalStatus === 'Complete') ||
            (item.initialStatus !== 'Complete' && item.isMandatory !== 'Y')
        );
    
        if (allIndicatorsValid) {
            dispatch(toggleSpinner(true));
            navigate(`/impact-maker/content/final/case-studies/${ID}`);
        } else {
            message.error('Please ensure all required indicators are complete before proceeding.');
        }
    }  

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        setTimeout(() => {
            form.submit();
        }, 300);
    }

    const handlePrevious = () => {
        navigate(`/impact-maker/content/final/objectives/${ID}`);
    }

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <Steps
                current={1}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_1" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "inputs_n_outputs" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_2" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>
                            {intl.formatMessage({ id: "objectives_n_outcomes" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_3" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "case_studies" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_4" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "next_steps" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_5" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "preview_n_submit" })}
                        </StyledText>,
                    },
                ]}
            />
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>
                        {intl.formatMessage({ id: "impact_measures" })}
                    </StyledText>
                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        form={form}
                        className="formContainer"
                        scrollToFirstError
                    >
                        <div className="mainContentContainer">
                            <div className="contentContainer">
                                <StyledText as="p" variant="B3">
                                    {intl.formatMessage({ id: "the_company_has_requested_you_to_add_indicators_to_measure_the_progress_of_your_project" })}
                                </StyledText>
                                <CharityCreateIndicators opportunityId={opportunityID} programID={programID} />
                                <div className='buttonContainer'>
                                    <Divider />
                                    <div className='buttonContentContainer'>
                                        <div className='nextAndPreviousContainer'>
                                            <Button
                                                variant="secondary"
                                                type="button"
                                                onClick={() => handlePrevious()}>
                                                {intl.formatMessage({ id: "previous" })}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                htmlType="submit"
                                            >
                                                {intl.formatMessage({ id: "next" })}
                                            </Button>
                                        </div>
                                        <div className="saveAndExitButton">
                                            <Button
                                                variant="secondary"
                                                htmlType="button"
                                                onClick={() => handleSaveAndExit()}>
                                                {intl.formatMessage({ id: "save_n_exit" })}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="buttonContainer">
                                <Divider />
                                <div className="buttonContentContainer">
                                    <div className="nextAndPreviousContainer">
                                        <Button variant="secondary" type="button" onClick={() => handlePrevious()}>
                                            {intl.formatMessage({ id: "previous" })}
                                        </Button>
                                        <Button variant="primary" htmlType="submit">
                                            {intl.formatMessage({ id: "next" })}
                                        </Button>
                                    </div>
                                    <div className="saveAndExitButton">
                                        <Button variant="secondary" htmlType="button" onClick={() => handleSaveAndExit()}>
                                            {intl.formatMessage({ id: "save_n_exit" })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    )
}

const StyledCharityInterimCreateReport = styled(CharityInterimCreateReport)`
  &.charityInterimCreateReport {
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    justify-content: space-between;
    margin-top: 50px;
    .inputElement .ant-form-item-control-input{
        width: 200px;
    }

    .whiteSupportContainer{
        background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          width: 35vw;
          &.volunteeringContainer {
         box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
    }

    .smallInput{
        width: 500px;
    }
 .collapseChildContainer {
      border: 1px solid #d7dde1;
      padding: 12px 16px;
      margin-bottom: 6px;
      cursor: pointer;
      text-align: left;
      background: white;
      font-size: 14px;
      font-weight: ${constants.FONT_WEIGHT_SEMIBOLD};
      color: ${constants.COLOR_GREY_DARK};
      font-family: ${constants.POPPINS_FONT_FAMILY};

      &.selectedName {
        border: 1px solid #04ac9c;
        border-radius: 6px;
        background: #bcfbf5;
      }
    }

    .collapsible {
      display: flex;
      flex-direction: column;

      .collapseChildContainer {
        background: white;
        border: 0;
        border-bottom: 1px solid #d9d9d9;
        padding: 12px 16px;
        text-align: left;

        &.selectedName {
          border: 1px solid #04ac9c;
          background: #bcfbf5;
        }

        cursor: pointer;
      }
    }

    .subContainer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      .descriptionContainer{
        max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
        p, li{
            font-size: 14px;
            font-weight: 500;
            color: #242424;
            font-family: sans-serif;
            max-width: 70ch;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }
      }
    }

    .ant-collapse-expand-icon {
      transform: rotate(90deg) translate(-5px);
      svg{
            fill: #313131 !important;
        }
    }

    .ant-collapse-item-active {
      .ant-collapse-expand-icon {
        transform: rotate(0deg) translate(-5px);
      }
    }
    .mainTitle {
      margin-bottom: 20px;
    }

    .contentContainer {
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 60vh;
      padding-bottom: 60px;

      .formContainer {
        display: flex;
        gap: 12px;
        flex-direction: column;
      }
    }

    .overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.45);
    }

    .spinner {
      z-index: 2000;
      margin: auto;
      left: 50%;
      right: 50%;
      position: absolute;
      bottom: 50%;
    }

    .radioGroup {
      display: flex;
      flex-direction: column;
    }

    .formItemTextField {
      .ant-form-item-control-input {
        width: 34.5vw !important;
      }
    }

    .formItemTextFieldPart {
      .ant-form-item-control-input {
        width: 16vw !important;
      }
    }

    .helperText {
      color: #5271FF !important;
      font-weight: bold !important;
      margin-bottom: 0px !important;
    }

    .ant-form-item {
      margin-bottom: 10px;
    }

    .formItemInput {
      width: 300px;
    }

    .mainTitle{
        margin-bottom: 20px;
    }
    .errorText{
        color: #ff4d4f;
    }
    .contentContainer{
        display: flex;
    }
    .addButton{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .optionsContainer{
        display: flex;
        align-items: center;
        gap: 30px;
        .for{
            margin-bottom: 10px;
        }
    }

    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -110px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .formItemTextField{
        .ant-form-item-control-input{
            width: 34.5vw !important;
        }
    }
    .formItemTextFieldPart{
        .ant-form-item-control-input{
            width: 16vw !important;
        } 
    }
    .helperText {
    color: #5271FF !important;
    font-weight: bold !important;
    margin-bottom: 0px !important;
  }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .formItemInput{
    width: 300px;
  }
  }
`;

export default StyledCharityInterimCreateReport;
